import { useFrame } from '@react-three/fiber'
import { useEffect, useRef, useState } from 'react'
import { Html } from '@react-three/drei'
import { DoubleSide, type Group } from 'three'
import { Rotulo3D } from '../componentes/Rotulo3D'
import type { EstadoZonaOperacao } from './estadoZonas'

/**
 * Ponto de operação da oficina de corte.
 *
 * Antes era uma esfera amarela de 7,5 cm, igual em todos os pontos e do começo
 * ao fim do exercício: não dizia o que era aquele ponto, nem se a ferramenta já
 * tinha passado por ali. Aqui o marcador é uma mira — anel assentado no plano
 * da peça, núcleo com volume e rótulo sob o ponteiro —, e o desenho muda com o
 * estado: pendente, mexido ou concluído (aí o núcleo dá lugar a duas pontas
 * separadas, o registro visível do que foi cortado).
 *
 * O anel desenha sem teste de profundidade porque os pontos da tampa nascem
 * sobre a própria chapa; sem isso, metade do anel ficaria dentro dela.
 */

const CORES: Record<EstadoZonaOperacao, { base: string; emissiva: string }> = {
  pendente: { base: '#f5c451', emissiva: '#6b4e0c' },
  trabalhada: { base: '#ff9a3c', emissiva: '#7a3c08' },
  concluida: { base: '#4fd88a', emissiva: '#12613a' },
}

interface Props {
  posicao: [number, number, number]
  /** Rotação que deita o anel no plano da peça. */
  rotacao: [number, number, number]
  nome: string
  descricao?: string
  disponivel?: boolean
  areaContato?: readonly [altura: number, largura: number]
  progressoEstrutural?: number
  estado: EstadoZonaOperacao
  selecionada: boolean
  /** Nome do objeto na cena, para depuração e para achar o marcador em testes. */
  nomeObjeto: string
  aoSelecionar: () => void
  /** Pressionar já começa a fechar a ferramenta — soltar interrompe (ver App.tsx). */
  aoPressionarFerramenta: () => void
  aoSoltarFerramenta?: () => void
  aoPrevisualizar?: () => void
}

export function MarcadorZona3D({
  posicao,
  rotacao,
  nome,
  descricao = 'Selecionar ponto de aplicação',
  disponivel = true,
  areaContato = [0.2, 0.2],
  progressoEstrutural = 0,
  estado,
  selecionada,
  nomeObjeto,
  aoSelecionar,
  aoPressionarFerramenta,
  aoSoltarFerramenta = () => undefined,
  aoPrevisualizar = () => undefined,
}: Props) {
  const [sobre, setSobre] = useState(false)
  const pulsoRef = useRef<Group>(null)

  useEffect(() => {
    if (!sobre || typeof document === 'undefined') return
    const anterior = document.body.style.cursor
    document.body.style.cursor = 'pointer'
    return () => {
      document.body.style.cursor = anterior
    }
  }, [sobre])

  // Só o ponto selecionado pulsa: é o que a ferramenta vai atingir, e o pulso
  // é o que o distingue à distância sem depender de cor.
  useFrame(({ clock }) => {
    const grupo = pulsoRef.current
    if (!grupo) return
    grupo.scale.setScalar(selecionada ? 1 + Math.sin(clock.elapsedTime * 3.4) * 0.1 : 1)
  })

  const cor = CORES[estado]
  const concluida = estado === 'concluida'
  const destaque = selecionada || sobre

  return (
    <group
      position={posicao}
      name={nomeObjeto}
      onPointerDown={(evento) => {
        if (evento.nativeEvent.button !== 0) return
        evento.stopPropagation()
        aoSelecionar()
        aoPressionarFerramenta()
      }}
      onPointerOver={(evento) => {
        evento.stopPropagation()
        setSobre(true)
        aoPrevisualizar()
      }}
      onPointerOut={() => setSobre(false)}
    >
      {/* A área invisível acompanha a região real, em vez de exigir acertar uma bolinha. */}
      <mesh rotation={rotacao}>
        <planeGeometry args={[areaContato[1], areaContato[0]]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* Alvo acessível equivalente ao gesto: Tab seleciona; Espaço/Enter segura e solta. */}
      <Html position={[0, 0, 0]} center zIndexRange={[0, 0]} style={{ pointerEvents: 'none' }}>
        <button
          type="button"
          className="alvo-oficina-acessivel"
          aria-label={`${nome}. ${descricao}`}
          onFocus={() => {
            aoSelecionar()
            aoPrevisualizar()
          }}
          onKeyDown={(evento) => {
            if ((evento.key !== 'Enter' && evento.key !== ' ') || evento.repeat) return
            evento.preventDefault()
            aoSelecionar()
            aoPressionarFerramenta()
          }}
          onKeyUp={(evento) => {
            if (evento.key !== 'Enter' && evento.key !== ' ') return
            evento.preventDefault()
            aoSoltarFerramenta()
          }}
        />
      </Html>

      <group ref={pulsoRef}>
        <group rotation={rotacao}>
          <mesh renderOrder={3}>
            <ringGeometry args={[0.052, 0.068, 32]} />
            <meshBasicMaterial
              color={cor.base}
              transparent
              opacity={destaque ? 0.95 : 0.62}
              side={DoubleSide}
              depthTest={false}
              depthWrite={false}
            />
          </mesh>

          {destaque && (
            <mesh renderOrder={3}>
              <ringGeometry args={[0.086, 0.095, 32]} />
              <meshBasicMaterial
                color={cor.base}
                transparent
                opacity={0.45}
                side={DoubleSide}
                depthTest={false}
                depthWrite={false}
              />
            </mesh>
          )}

          {/* concluído: duas pontas com folga entre elas, no lugar do núcleo */}
          {concluida && (
            <group rotation={[0, 0, Math.PI * 0.22]}>
              {[-1, 1].map((lado) => (
                <mesh key={lado} position={[lado * 0.03, 0, 0]} renderOrder={3}>
                  <planeGeometry args={[0.032, 0.012]} />
                  <meshBasicMaterial
                    color={cor.base}
                    transparent
                    opacity={0.95}
                    side={DoubleSide}
                    depthTest={false}
                    depthWrite={false}
                  />
                </mesh>
              ))}
            </group>
          )}
        </group>

        {!concluida && (
          <mesh scale={destaque ? 1.18 : 1}>
            <sphereGeometry args={[0.03, 18, 12]} />
            <meshStandardMaterial
              color={cor.base}
              emissive={cor.emissiva}
              emissiveIntensity={selecionada ? 1.1 : 0.55}
              roughness={0.35}
              metalness={0.1}
            />
          </mesh>
        )}
      </group>

      {destaque && (
        <Rotulo3D
          posicao={[0, 0.17, 0]}
          texto={nome}
          subtexto={progressoEstrutural > 0 && progressoEstrutural < 1
            ? `${descricao} · ${Math.round(progressoEstrutural * 100)}% realizado`
            : descricao}
          variante={disponivel ? 'zona' : 'risco'}
          fator={5}
        />
      )}
    </group>
  )
}
